/**
 * What Joogoos is doing for this business, in the business's own words (docs/27, ADR 0045).
 *
 * The API answers in codes — what the work is, what state it is in, what it is about, how far along — and this
 * module is the one place they become sentences. Never *job*, *queue*, *worker*, *call*, *render* or *workspace*:
 * the machine is not what somebody is looking at. No React here, so every code is held to a sentence by
 * `activity.test.ts` without a browser, as the analysis steps are.
 *
 * Deliberately one flat list rather than a section per state. There are rarely more than a handful of these at
 * once, the panel groups them itself, and a list that arrives already grouped is a shape the next state would
 * have to be added to in three places.
 */
import type { ProjectKind } from "./api.js";

/** The kinds of work a business can see. One code per row the API can point at. */
export type ActivityWork =
  | "reading_business"
  | "making_post"
  | "making_video"
  | "putting_together"
  | "writing_article"
  | "publishing_post"
  | "reading_companies"
  | "packing_export";

/**
 * Three states and not five. *Running* and *waiting* are the difference between "it is happening" and "it is in
 * line", which is the whole question somebody watching has; *stopped* is the only one they can do anything about.
 * Nothing finished is carried: a thing that is done is on its own screen, done, and a list of the recently
 * finished is a list nobody reads twice.
 */
export type ActivityState = "running" | "waiting" | "stopped";

/** Which tab to open to look at a piece of work. The names are the tabs' own (`App.tsx`). */
export type ActivityTab = "Overview" | "Business" | "Studio" | "Content" | "Calendar" | "Analytics" | "Customers" | "SEO";

export interface ActivityItem {
  /** The row this is about: a key for the list, and what a click carries. */
  id: string;
  work: ActivityWork;
  state: ActivityState;
  /** What it is about, in the owner's own words: a post's title, an article's phrase, the name of a platform. */
  about: string | null;
  /** How far along, where the work counts in parts. */
  step: { done: number; of: number } | null;
  /** The step's own label where the work has one to say — the business lookup's "Finding you on Google". */
  stepLabel: string | null;
  /** How many things one row stands for: 4 emails, 3 companies. Null when it is one thing. */
  count: number | null;
  /** When it started, or when it was put in line. */
  at: string | null;
  /** Where to go and look. Null when there is nowhere better than where you are. */
  tab: ActivityTab | null;
  /** The post this is about, so the Studio can open it rather than whatever was open last. */
  projectId: string | null;
}

/** `GET /v1/activity` */
export interface ActivityResponse {
  counts: { running: number; waiting: number; stopped: number };
  /** Stopped first, then running, then waiting; oldest first inside each. */
  items: ActivityItem[];
  asOf: string;
}

/** `GET /v1/activity/render-wait`: how long a video sent now waits for a render container, in whole minutes. */
export interface RenderWaitResponse { minutes: number }

/** From this long the plan says the queue is busy before Generate (docs/10 § Queues: backpressure). */
export const RENDER_BUSY_MINUTES = 10;

/** Nothing happening, which is what a browser draws before the first answer arrives. */
export const ACTIVITY_QUIET: ActivityResponse = { counts: { running: 0, waiting: 0, stopped: 0 }, items: [], asOf: "" };

/** The order the panel and the list are in: what needs you, then what is happening, then what is in line. */
const ORDER: Record<ActivityState, number> = { stopped: 0, running: 1, waiting: 2 };
export const byActivityState = (a: ActivityItem, b: ActivityItem) =>
  ORDER[a.state] - ORDER[b.state] || (a.at ?? "").localeCompare(b.at ?? "");

/**
 * The indicator itself: a tone, and the few words beside it. Null while there is nothing to say, because an
 * indicator that is always there is furniture and stops being read.
 *
 * Stopped leads whatever else is happening — it is the only state somebody can act on. Otherwise anything running
 * spins, and a queue with nothing running yet is amber: the difference matters to somebody who has just pressed a
 * button and is wondering whether anything happened.
 */
export function activityIndicator(counts: ActivityResponse["counts"]): { state: "working" | "waiting" | "stopped"; label: string } | null {
  if (counts.stopped > 0) return { state: "stopped", label: `${counts.stopped} stopped` };
  if (counts.running > 0) {
    const n = counts.running + counts.waiting;
    return { state: "working", label: n === 1 ? "Working on it" : `Working on ${n}` };
  }
  if (counts.waiting > 0) return { state: "waiting", label: `${counts.waiting} waiting` };
  return null;
}

/** A row of the panel: the line in bold, and the muted line under it. */
export interface ActivitySaid { title: string; detail: string }

const quoted = (about: string | null) => (about ? `“${about}”` : "it");
const things = (n: number | null, one: string, many = `${one}s`) => `${n ?? 1} ${(n ?? 1) === 1 ? one : many}`;

/**
 * One piece of work, said.
 *
 * Every code gets a sentence in each state it can be in, and the test holds it to that: a code with no words is a
 * row reading "undefined" on somebody's screen, and there is no safe fallback that is also honest.
 */
export function activitySaid(item: ActivityItem): ActivitySaid {
  const detail = item.state === "waiting" ? "starts soon" : soFar(item);
  return { title: titleOf(item), detail };
}

function titleOf(item: ActivityItem): string {
  const { work, state, about, count } = item;
  if (state === "stopped") {
    switch (work) {
      case "reading_business": return "Could not read your business";
      // At its cap or because a step gave up: the one line under it says what to do about either (2026-09-25).
      case "making_post": case "making_video": return `${quoted(about)} stopped`;
      case "putting_together": return `Could not finish ${quoted(about)}`;
      case "writing_article": return `Could not write ${quoted(about)}`;
      case "publishing_post": return `Could not post to ${about ?? "your channel"}`;
      case "reading_companies": return "Could not read a company";
      case "packing_export": return "Could not pack everything up";
    }
  }
  switch (work) {
    case "reading_business": return "Reading your business";
    case "making_post": return `Making ${quoted(about)}`;
    case "making_video": return `Making ${quoted(about)}`;
    case "putting_together": return `Putting ${quoted(about)} together`;
    case "writing_article": return `Writing ${quoted(about)}`;
    case "publishing_post": return `Posting to ${about ?? "your channel"}`;
    case "reading_companies": return `Reading ${things(count, "company", "companies")}`;
    case "packing_export": return "Packing up everything you have";
  }
}

/** The muted line: the step it is on, how far along it is, or what it will do next. */
function soFar(item: ActivityItem): string {
  if (item.state === "stopped") return stopped(item.work);
  if (item.stepLabel) return item.stepLabel;
  if (item.step) return `${item.step.done} of ${item.step.of} done`;
  return "";
}

function stopped(work: ActivityWork): string {
  switch (work) {
    case "reading_business": return "Ask again from the Business tab";
    case "making_post": case "making_video": return "Try it again, or raise its cap, from the post";
    case "putting_together": return "Try it again from the post";
    case "writing_article": return "Try it again from SEO";
    case "publishing_post": return "See why, and retry it, on the Calendar";
    case "reading_companies": return "It is tried again by itself";
    case "packing_export": return "Ask for it again from your account";
  }
}

/**
 * How close a post has to be to its time to count as going out (27). "Post it" writes one for a minute from now and
 * the sweep takes it within the minute after, so two minutes covers the press; a post scheduled for ten o'clock
 * shows from two minutes to ten, which is what it is doing by then.
 */
export const POST_GOING_OUT_MS = 2 * 60 * 1000;

/** The line under "Posting to Instagram": in line for its minute, or with the platform now. */
export const postGoingOut = (sending: boolean): string => (sending ? "Sending it now" : "Goes out in about a minute");

/** Which work a generating project is: the same choice the rail makes, kept in one place. */
export const makingWork = (kind: ProjectKind | null): ActivityWork => (kind === "video" ? "making_video" : "making_post");
